import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "@/components/layouts/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Calendar,
  MapPin,
  Users,
  ArrowLeft,
  BookmarkPlus,
  Share2,
} from "lucide-react";
import { getEventById } from "@/lib/api/events";
import { EventData } from "@/types/events";
import { useLanguage } from "@/contexts/LanguageContext"; 

const detectTextDirection = (value: string, fallback: "ltr" | "rtl"): "ltr" | "rtl" => {
  const text = value
    .replace(/<[^>]*>/g, " ")
    .replace(/&[a-zA-Z0-9#]+;/g, " ");
  const arabicCharacters = text.match(/\p{Script=Arabic}/gu)?.length ?? 0;
  const latinCharacters = text.match(/\p{Script=Latin}/gu)?.length ?? 0;

  if (arabicCharacters === latinCharacters) return fallback;
  return arabicCharacters > latinCharacters ? "rtl" : "ltr";
};

const EventDetailPage = () => {
  const { eventId } = useParams();
  const { language } = useLanguage(); 
  const [event, setEvent] = useState<EventData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);


  useEffect(() => {
    const fetchEvent = async () => {
      try {
        setLoading(true);
        setError(null);
        const eventData = await getEventById(eventId!, language); 
        setEvent(eventData);
      } catch (err) {
        console.error("Error fetching event:", err);
        setError(err instanceof Error ? err.message : "Failed to load event");
        setEvent(null);
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [eventId, language]);

  if (loading) {
    return (
      <Layout>
        <div className="container py-24 text-center text-gray-600">
          {language === "ar" ? "جاري تحميل تفاصيل الحدث..." : "Loading event details..."}
        </div>
      </Layout>
    );
  }

  if (!event) {
    return (
      <Layout>
        <div className="container py-24 text-center">
          <h1 className="text-2xl font-bold text-nexus-gray mb-4">
            {language === "ar" ? "لم يتم العثور على الحدث" : "Event Not Found"}
          </h1>
          <p className="text-gray-600 mb-8">
            {language === "ar"
              ? "عذرًا، لم نتمكن من العثور على هذا الحدث."
              : "Sorry, we couldn’t find this event."}
          </p>
          <Button asChild>
            <Link to="/events">{language === "ar" ? "العودة إلى الأحداث" : "Back to Events"}</Link>
          </Button>
        </div>
      </Layout>
    );
  }

  // Parse strings into bullet points
  const agenda = event.agenda?.split("\n").filter(Boolean) ?? [];
  const prerequisites = event.prerequisites?.split("\n").filter(Boolean) ?? [];
  const whatYoullLearn = event.whatYoullLearn?.split("\n").filter(Boolean) ?? [];
  const fullDescription = event.fullDescription ?? event.description;
  const pageDirection = language === "ar" ? "rtl" : "ltr";
  const titleDirection = detectTextDirection(event.title, pageDirection);
  const descriptionDirection = detectTextDirection(fullDescription, pageDirection);

  return (
    <Layout>
      <div
        className={`container py-8 ${language === "ar" ? "text-right" : "text-left"}`}
        dir={language === "ar" ? "rtl" : "ltr"}
      >
        <Button variant="ghost" asChild className="mb-6">
          <Link to="/events" className="flex items-center gap-2">
            <ArrowLeft className={`h-4 w-4 ${language === "ar" ? "rotate-180" : ""}`} />
            {language === "ar" ? "العودة إلى الأحداث" : "Back to Events"}
          </Link>
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="mb-6">
              <div className="flex flex-wrap gap-2 mb-4">
                <Badge
                  className={
                    event.type?.[language] === "Webinar"
                      ? "bg-nexus-teal/20 text-nexus-teal"
                      : event.type?.[language] === "Workshop"
                      ? "bg-nexus-yellow/20 text-nexus-gray"
                      : "bg-nexus-red/20 text-nexus-red"
                  }
                >
                  {event.type?.[language]}
                </Badge>
                {event.badge && (
                  <Badge className="bg-nexus-yellow text-nexus-gray">{event.badge}</Badge>
                )}
              </div>

              <h1
                className={`text-3xl md:text-4xl font-bold text-nexus-gray dark:text-white mb-4 ${
                  titleDirection === "rtl" ? "text-right" : "text-left"
                }`}
                dir={titleDirection}
              >
                {event.title}
              </h1>
              {fullDescription ? (
                <div
                  className={`event-rich-text text-lg text-gray-600 dark:text-nexus-light mb-6 prose prose-lg max-w-none dark:prose-invert ${
                    descriptionDirection === "rtl" ? "text-right" : "text-left"
                  }`}
                  dir={descriptionDirection}
                  dangerouslySetInnerHTML={{ __html: fullDescription }}
                />
              ) : (
                <p className="text-lg text-gray-600 dark:text-nexus-light mb-6">
                  No description available
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 text-gray-600 dark:text-nexus-light">
              <div className="flex items-center gap-3">
                <Calendar className="h-5 w-5 text-nexus-teal" />
                <div>
                  <div className="font-medium">{event.date}</div>
                  <div className="text-sm">{event.time}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Users className="h-5 w-5 text-nexus-teal" />
                <div>
                  <div className="font-medium">{event.speaker?.name}</div>
                  <div className="text-sm">
                    {language === "ar"
                      ? `${event.attendees} مشارك`
                      : `${event.attendees} attending`}
                  </div>
                </div>
              </div>
              {event.location && (
                <div className="flex items-center gap-3">
                  <MapPin className="h-5 w-5 text-nexus-teal" />
                  <div className="font-medium">{event.location}</div>
                </div>
              )}
            </div>

            {agenda.length > 0 && (
              <Card className="mb-8">
                <CardContent className="p-6">
                  <h2 className="text-xl font-semibold text-nexus-gray dark:text-nexus-light mb-4">
                    {language === "ar" ? "جدول الأعمال" : "Agenda"}
                  </h2>
                  <ul className="space-y-2">
                    {agenda.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <span className="w-6 h-6 bg-nexus-teal/10 text-nexus-teal rounded-full flex items-center justify-center text-sm font-medium">
                          {idx + 1}
                        </span>
                        <span className="text-gray-600 dark:text-nexus-light">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {whatYoullLearn.length > 0 && (
              <Card className="mb-8">
                <CardContent className="p-6">
                  <h2 className="text-xl font-semibold text-nexus-gray dark:text-nexus-light mb-4">
                    {language === "ar" ? "ما ستتعلمه" : "What You'll Learn"}
                  </h2>
                  <ul className="space-y-2">
                    {whatYoullLearn.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-nexus-teal">•</span>
                        <span className="text-gray-600 dark:text-nexus-light">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {prerequisites.length > 0 && (
              <Card className="mb-8">
                <CardContent className="p-6">
                  <h2 className="text-xl font-semibold text-nexus-gray dark:text-nexus-light mb-4">
                    {language === "ar" ? "المتطلبات المسبقة" : "Prerequisites"}
                  </h2>
                  <ul className="space-y-2">
                    {prerequisites.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-nexus-teal">•</span>
                        <span className="text-gray-600 dark:text-nexus-light">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {event.speaker?.bio && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-xl font-semibold text-nexus-gray mb-4">
                    {language === "ar" ? "حول المتحدث" : "About the Speaker"}
                  </h2>
                  <p className="text-gray-600 dark:text-nexus-light">{event.speaker?.bio}</p>
                </CardContent>
              </Card>
            )}
          </div>

          <div className="lg:col-span-1 sticky top-24 space-y-6">
            <Card>
              <CardContent className="p-6 text-center">
                <div className="text-2xl font-bold text-nexus-gray dark:text-nexus-light mb-2">
                  {language === "ar" ? "حدث مجاني" : "Free Event"}
                </div>
                <div className="text-gray-600 dark:text-nexus-light mb-6">
                  {language === "ar"
                    ? `انضم إلى ${event.attendees} آخرين`
                    : `Join ${event.attendees} others`}
                </div>
                {event.GoogleFormURL ? (
                  <Button asChild className="w-full bg-nexus-brand hover:bg-nexus-brand/90 mb-3">
                    <a href={event.GoogleFormURL} target="_blank" rel="noopener noreferrer">
                      {language === "ar" ? "سجل الآن" : "Register Now"}
                    </a>
                  </Button>
                ) : (
                  <Button disabled className="w-full bg-gray-200 text-gray-500 mb-3">
                    {language === "ar" ? "التسجيل غير متاح" : "Registration Unavailable"}
                  </Button>
                )}
                <div className="flex gap-2">
                  {/* <Button variant="outline" size="sm" className="flex-1">
                    <BookmarkPlus className="h-4 w-4 mr-1" />
                    {language === "ar" ? "حفظ" : "Save"}
                  </Button> */}
                  <Button variant="outline" size="sm" className="flex-1">
                    <Share2 className="h-4 w-4 mr-1" />
                    {language === "ar" ? "مشاركة" : "Share"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default EventDetailPage;
